import { useUserData } from 'hooks/use-user-data';
import * as Papa from 'papaparse';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHref } from 'react-router';
import { DragStart, DropResult } from 'react-beautiful-dnd';
import useErrorStore from 'stores/error-store';
import isEmail from 'validator/lib/isEmail';
import * as XLSX from 'xlsx';
import { addSurveyTag, deleteSurvey, editSurveyTag, getAllSurveys, getSurveyInviteLink, getSurveyTags, moveSurveysToTag, sendOpenSurvey } from '../../../API/surveys';
import { Survey, SurveyPayload, SurveyTag, TagModel } from '../survey.types';
import { ImageTypes, Routes } from '../../../enums';

export interface SingleForm {
  email: { text: string; error: boolean };
  lastName: { text: string; error: boolean };
  firstName: { text: string; error: boolean };
}

export interface SelectionState {
  [key: string]: boolean;
}

interface TagsState {
  tags: TagModel[];
  tagsOrder: number[];
}

interface SendInvitationsState {
  open: boolean;
  selectedFolder: string;
  selectedSurveyType: string;
  loading: boolean;
  notSendEmail: boolean;
}

interface TagDialogState {
  open: boolean;
  tagName: string;
  loading: boolean;
}

interface EditTagDialogState extends TagDialogState {
  tagId: string;
}

interface SurveysMoveState {
  dialogOpen: boolean;
  sourceTagName: string;
  sourceTagId: string;
  destinationTagId: string;
  destinationTagName: string;
  analysesCount: number;
  sourceAnalyses: Survey[];
  destinationAnalyses: Survey[];
  toMoveAnalyses: Survey[];
  loading: boolean;
}

interface ConfirmDeleteState {
  open: boolean;
  currentTag?: TagModel;
  selectionCount: number;
}

interface ExportSurveyRow {
  tagName: string;
  survey: Survey;
}

export interface CsvImportSummary {
  fileName: string;
  importedCount: number;
  invalidEmailCount: number;
  duplicateCount: number;
  ignoredRowCount: number;
  truncatedCount: number;
  hasHeader: boolean;
  hasError: boolean;
}

export interface CsvPreviewRow {
  rowNumber: number;
  email: string;
  lastName: string;
  firstName: string;
  status: 'valid' | 'invalid_email' | 'duplicate' | 'ignored' | 'truncated';
}

const EMPTY_FORM: SingleForm = {
  email: { text: '', error: false },
  lastName: { text: '', error: false },
  firstName: { text: '', error: false },
};
const MAX_CSV_INVITATIONS = 1000;

export function useSurveyManagement() {
  const { t } = useTranslation();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const { isAuthenticated, token, tenant } = useUserData();
  const authenticated = isAuthenticated();

  // Core state
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [tagsState, setTagsState] = useState<TagsState>({ tags: [], tagsOrder: [] });
  const [draggedAnalysisId, setDraggedAnalysisId] = useState('');
  const [selectionState, setSelectionState] = useState<SelectionState>({});

  // Dialog states
  const [sendInvitationsState, setSendInvitationsState] = useState<SendInvitationsState>({
    open: false,
    selectedFolder: '',
    loading: false,
    selectedSurveyType: ImageTypes.CLASSIC_IMAGE,
    notSendEmail: false,
  });
  const [sendInvitationsFormState, setSendInvitationsFormState] = useState<SingleForm[]>([{ ...EMPTY_FORM }]);
  const [newTagDialogState, setNewTagDialogState] = useState<TagDialogState>({ open: false, tagName: '', loading: false });
  const [editTagDialogState, setEditTagDialogState] = useState<EditTagDialogState>({ open: false, tagName: '', tagId: '', loading: false });
  const [surveysMoveState, setSurveysMoveState] = useState<SurveysMoveState>({
    dialogOpen: false,
    sourceTagId: '',
    destinationTagId: '',
    sourceTagName: '',
    destinationTagName: '',
    analysesCount: 0,
    sourceAnalyses: [],
    destinationAnalyses: [],
    toMoveAnalyses: [],
    loading: false,
  });
  const [confirmDeleteState, setConfirmDeleteState] = useState<ConfirmDeleteState>({ open: false, selectionCount: 0 });
  const [csvImportSummary, setCsvImportSummary] = useState<CsvImportSummary | null>(null);
  const [csvPreviewRows, setCsvPreviewRows] = useState<CsvPreviewRow[]>([]);

  // Dynamic route for survey links
  const pathname = generatePath(`${Routes.SURVEY}/`);
  const path = useHref(pathname);
  const dynamicRoute = `${window.location.origin}${path}`;

  // --- Data fetching ---

  const fetchSurveys = async (tags: SurveyTag[]) => {
    if (!authenticated) return;
    setLoading(true);
    const userSurveys = await getAllSurveys();
    if (!userSurveys.error) {
      setTagsState((prev) => ({ ...prev, tags }));
    }
    setLoading(false);
  };

  const fetchInitialData = React.useCallback(async () => {
    if (!authenticated) return;
    setLoading(true);
    const [userTags, userSurveys] = await Promise.all([getSurveyTags(), getAllSurveys()]);
    if (userTags.error || userSurveys.error) {
      setLoading(false);
      return;
    }
    const tags = userTags.data;
    if (tags) {
      setTagsState({ tags, tagsOrder: tags.map((_, index) => index) });
    }
    setLoading(false);
  }, [authenticated]);

  React.useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // --- Tag helpers ---

  const getTagAnalyses = (tagId: string): Survey[] => tagsState.tags.find((tag) => tag.tagId === tagId)?.surveys ?? [];

  const getTagName = (tagId: string): string => tagsState.tags.find((tag) => tag.tagId === tagId)?.tagName ?? '';

  const getSelectionCount = (tagId: string): number =>
    getTagAnalyses(tagId).reduce((count, survey) => count + (selectionState[survey.id] ? 1 : 0), 0);

  // --- Tag edit handlers ---

  const handleEditTagClick = useCallback(
    (tagId: string) => () => {
      const toEditTag = tagsState.tags.find((tag) => tag.tagId === tagId);
      if (toEditTag) {
        setEditTagDialogState({ open: true, tagName: toEditTag.tagName, tagId, loading: false });
      }
    },
    [tagsState]
  );

  const handleEditTagChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditTagDialogState((prev) => ({ ...prev, tagName: event.target.value }));
  };

  const handleEditTagClose = () => {
    setEditTagDialogState((prev) => ({ ...prev, open: false }));
  };

  const handleEditTagProceed = async () => {
    setEditTagDialogState((prev) => ({ ...prev, loading: true }));
    const results = await editSurveyTag(editTagDialogState.tagId, editTagDialogState.tagName);
    if (!results.error) {
      setTagsState((prev) => ({
        ...prev,
        tags: prev.tags.map((tag) => (tag.tagId === editTagDialogState.tagId ? { ...tag, tagName: editTagDialogState.tagName } : tag)),
      }));
      setEditTagDialogState((prev) => ({ ...prev, open: false, loading: false }));
    } else {
      handleOpenErrorDialog(results?.error, '/surveys/tags/:tagId', 'patch');
    }
  };

  const handleToggleVisibility = (tagId: string, visibility?: boolean) => () => {
    (async () => {
      const results = await editSurveyTag(tagId, undefined, !!visibility);
      if (!results.error) {
        setTagsState((prev) => ({
          ...prev,
          tags: prev.tags.map((tag) => (tag.tagId === tagId ? { ...tag, visible: visibility } : tag)),
        }));
      } else {
        handleOpenErrorDialog(results?.error, '/surveys/tags/:tagId', 'patch');
      }
    })();
  };

  // --- Tag creation handlers ---

  const handleNewTagClick = () => {
    setNewTagDialogState((prev) => ({ ...prev, open: true }));
  };

  const handleNewTagChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewTagDialogState((prev) => ({ ...prev, tagName: event.target.value }));
  };

  const handleNewTagDialogClose = () => {
    setNewTagDialogState((prev) => ({ ...prev, open: false }));
  };

  const handleNewTagProceed = async () => {
    setNewTagDialogState((prev) => ({ ...prev, loading: true }));
    const results = await addSurveyTag(newTagDialogState.tagName);
    if (!results.error) {
      setTagsState((prev) => {
        const newTags = [...prev.tags];
        const newOrder = [...prev.tagsOrder];
        newTags.push({ surveys: [], tagId: results, tagName: newTagDialogState.tagName, isOwn: true });
        newOrder.splice(0, 0, newTags.length - 1);
        return { tags: newTags, tagsOrder: newOrder };
      });
      setNewTagDialogState({ open: false, loading: false, tagName: '' });
    } else {
      handleOpenErrorDialog(results?.error, '/surveys/tags', 'post');
    }
  };

  // --- Selection & drag-drop handlers ---

  const handleCheckboxClick = useCallback(
    (id: string) => (_event: React.ChangeEvent, checked: boolean) => {
      setSelectionState((prev) => ({ ...prev, [id]: checked }));
    },
    []
  );

  const unselectAllAnalyses = () => {
    setSelectionState((prev) =>
      Object.keys(prev).reduce(
        (acc, analysisId) => {
          acc[analysisId] = false;
          return acc;
        },
        {} as SelectionState
      )
    );
  };

  const handleAnalysesDelete = useCallback(
    (tagId: string) => async () => {
      const tagAnalyses = getTagAnalyses(tagId);
      const tagNewAnalyses = tagAnalyses.filter((a) => !selectionState[a.id]);
      const toDeleteAnalyses = tagAnalyses.filter((a) => selectionState[a.id]);
      let successCount = 0;
      for (const analysis of toDeleteAnalyses) {
        const results = await deleteSurvey(analysis.id);
        if (!results) {
          tagNewAnalyses.push(analysis);
        } else {
          successCount++;
        }
      }
      if (successCount) {
        setTagsState((prev) => ({
          ...prev,
          tags: prev.tags.map((tag) => (tag.tagId === tagId ? { ...tag, surveys: tagNewAnalyses } : tag)),
        }));
        setConfirmDeleteState({ open: false, currentTag: undefined, selectionCount: 0 });
      } else {
        handleOpenErrorDialog(undefined, '/surveys/:id', 'delete');
      }
    },
    [tagsState, selectionState]
  );

  const handleTagDelete = useCallback(
    (tagId: string) => {
      setTagsState((prev) => ({
        ...prev,
        tagsOrder: prev.tagsOrder.filter((index) => prev.tags[index].tagId !== tagId),
      }));
    },
    [tagsState]
  );

  const handleDifferentTagsDropping = (sourceTagId: string, destinationTagId: string, sourceIndex: number, destinationIndex: number) => {
    const sourceAnalyses = getTagAnalyses(sourceTagId);
    const destinationAnalyses = getTagAnalyses(destinationTagId);

    if (getSelectionCount(sourceTagId) <= 1) {
      const draggedAnalysis = sourceAnalyses[sourceIndex];
      const newSource = Array.from(sourceAnalyses);
      const newDest = Array.from(destinationAnalyses);
      newSource.splice(sourceIndex, 1);
      newDest.splice(destinationIndex, 0, draggedAnalysis);

      setSurveysMoveState({
        analysesCount: 1,
        dialogOpen: true,
        sourceTagId,
        destinationTagId,
        sourceAnalyses: newSource,
        destinationAnalyses: newDest,
        sourceTagName: getTagName(sourceTagId),
        destinationTagName: getTagName(destinationTagId),
        toMoveAnalyses: [draggedAnalysis],
        loading: false,
      });
      return;
    }

    const toMove = sourceAnalyses.filter((a) => selectionState[a.id]);
    const newSource = sourceAnalyses.filter((a) => !selectionState[a.id]);
    const newDest = Array.from(destinationAnalyses);
    let insertIndex = destinationIndex;
    toMove.forEach((a) => {
      newDest.splice(insertIndex, 0, a);
      insertIndex++;
    });

    setSurveysMoveState({
      analysesCount: toMove.length,
      dialogOpen: true,
      sourceTagId,
      destinationTagId,
      sourceAnalyses: newSource,
      destinationAnalyses: newDest,
      toMoveAnalyses: toMove,
      sourceTagName: getTagName(sourceTagId),
      destinationTagName: getTagName(destinationTagId),
      loading: false,
    });
  };

  const handleOnDragStart = (start: DragStart) => {
    const analysisId = start.draggableId;
    if (!selectionState[analysisId]) {
      unselectAllAnalyses();
    }
    setDraggedAnalysisId(analysisId);
  };

  const handleOnDragEnd = (result: DropResult) => {
    const { destination, source, type, reason } = result;
    if (!destination || reason === 'CANCEL') {
      setDraggedAnalysisId('');
      return;
    }
    if (type === 'TAG') {
      const curTagIndex = tagsState.tagsOrder[source.index];
      const tagsOrder = Array.from(tagsState.tagsOrder);
      tagsOrder.splice(source.index, 1);
      tagsOrder.splice(destination.index, 0, curTagIndex);
      setTagsState((prev) => ({ ...prev, tagsOrder }));
      return;
    }
    setDraggedAnalysisId('');
    if (source.droppableId !== destination.droppableId) {
      handleDifferentTagsDropping(source.droppableId, destination.droppableId, source.index, destination.index);
    }
    unselectAllAnalyses();
  };

  // --- Move dialog handlers ---

  const handleMoveDialogConfirm = async () => {
    setSurveysMoveState((prev) => ({ ...prev, loading: true }));
    const results = await moveSurveysToTag(
      surveysMoveState.sourceTagId,
      surveysMoveState.destinationTagId,
      surveysMoveState.toMoveAnalyses.map((s) => ({ id: s.id }))
    );
    if (results) {
      setTagsState((prev) => ({
        ...prev,
        tags: prev.tags.map((tag) => {
          if (tag.tagId === surveysMoveState.sourceTagId) return { ...tag, surveys: surveysMoveState.sourceAnalyses };
          if (tag.tagId === surveysMoveState.destinationTagId) return { ...tag, surveys: surveysMoveState.destinationAnalyses };
          return tag;
        }),
      }));
    } else {
      handleOpenErrorDialog(undefined, '/surveys/:surveyId/tags', 'post');
      handleOpenErrorDialog(undefined, '/surveys/:surveyId/tags', 'delete');
    }
    setSurveysMoveState((prev) => ({ ...prev, dialogOpen: false, loading: false }));
  };

  const handleMoveDialogClose = () => {
    setSurveysMoveState((prev) => ({ ...prev, dialogOpen: false }));
  };

  // --- Send invitations handlers ---

  const onSendInvitations = () => {
    setSendInvitationsState((prev) => ({ ...prev, open: true }));
  };

  const handleSendInvitationsClose = () => {
    setSendInvitationsState((prev) => ({ ...prev, open: false }));
    setSendInvitationsFormState([{ ...EMPTY_FORM }]);
    setCsvImportSummary(null);
    setCsvPreviewRows([]);
  };

  const handleSelectedFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSendInvitationsState((prev) => ({ ...prev, selectedFolder: event.target.value }));
  };

  const handleSelectedSurveyTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSendInvitationsState((prev) => ({ ...prev, selectedSurveyType: event.target.value }));
  };

  const formChangeHandlerGenerator = (idx: number, type: 'email' | 'lastName' | 'firstName') => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const updated = [...sendInvitationsFormState];
      updated[idx][type].text = e.target.value;
      if (type === 'email') {
        updated[idx][type].error = !isEmail(updated[idx][type].text);
      }
      setSendInvitationsFormState(updated);
    };
  };

  const formChangeHandleDeleteGenerator = (idx: number) => () => {
    const updated = [...sendInvitationsFormState];
    updated.splice(idx, 1);
    setSendInvitationsFormState(updated);
  };

  const addInvitations = () => {
    if (!csvLoading) {
      setSendInvitationsFormState((prev) => [...prev, { ...EMPTY_FORM }]);
    }
  };

  const clearInvitations = () => {
    setSendInvitationsFormState([{ ...EMPTY_FORM }]);
    setCsvImportSummary(null);
    setCsvPreviewRows([]);
  };

  const parseInvitationsFile = (file: File, resetInput?: () => void) => {
    setCsvLoading(true);
    setCsvImportSummary(null);

    Papa.parse<string[]>(file, {
      skipEmptyLines: 'greedy',
      complete: (results) => {
        const sourceRows = (results.data ?? []) as string[][];

        if (sourceRows.length === 0) {
          setCsvImportSummary({
            fileName: file.name,
            importedCount: 0,
            invalidEmailCount: 0,
            duplicateCount: 0,
            ignoredRowCount: 0,
            truncatedCount: 0,
            hasHeader: false,
            hasError: true,
          });
          setCsvLoading(false);
          resetInput?.();
          return;
        }

        const firstCell = sourceRows[0]?.[0]?.trim().toLowerCase();
        const hasHeader = firstCell === 'email' || firstCell === 'e-mail';
        const rows = hasHeader ? sourceRows.slice(1) : sourceRows;

        const seenEmails = new Set<string>();
        let invalidEmailCount = 0;
        let duplicateCount = 0;
        let ignoredRowCount = 0;
        let truncatedCount = 0;
        const previewRows: CsvPreviewRow[] = [];

        const parsed: SingleForm[] = [];

        rows.forEach((item, index) => {
          const rowNumber = hasHeader ? index + 2 : index + 1;

          if (parsed.length >= MAX_CSV_INVITATIONS) {
            truncatedCount += 1;
            previewRows.push({
              rowNumber,
              email: (item?.[0] ?? '').trim(),
              lastName: (item?.[1] ?? '').trim(),
              firstName: (item?.[2] ?? '').trim(),
              status: 'truncated',
            });
            return;
          }

          const email = (item?.[0] ?? '').trim();
          const lastName = (item?.[1] ?? '').trim();
          const firstName = (item?.[2] ?? '').trim();

          if (!email && !lastName && !firstName) {
            ignoredRowCount += 1;
            previewRows.push({
              rowNumber,
              email,
              lastName,
              firstName,
              status: 'ignored',
            });
            return;
          }

          if (!email) {
            ignoredRowCount += 1;
            previewRows.push({
              rowNumber,
              email,
              lastName,
              firstName,
              status: 'ignored',
            });
            return;
          }

          const normalizedEmail = email.toLowerCase();
          if (seenEmails.has(normalizedEmail)) {
            duplicateCount += 1;
            previewRows.push({
              rowNumber,
              email,
              lastName,
              firstName,
              status: 'duplicate',
            });
            return;
          }
          seenEmails.add(normalizedEmail);

          const hasInvalidEmail = !isEmail(email);
          if (hasInvalidEmail) {
            invalidEmailCount += 1;
          }

          previewRows.push({
            rowNumber,
            email,
            lastName,
            firstName,
            status: hasInvalidEmail ? 'invalid_email' : 'valid',
          });

          parsed.push({
            email: { text: email, error: hasInvalidEmail },
            lastName: { text: lastName, error: false },
            firstName: { text: firstName, error: false },
          });
        });

        if (parsed.length > 0) {
          setSendInvitationsFormState((prev) => (prev.length > 0 && prev[0].email.text !== '' ? [...prev, ...parsed] : parsed));
        }

        setCsvImportSummary({
          fileName: file.name,
          importedCount: parsed.length,
          invalidEmailCount,
          duplicateCount,
          ignoredRowCount,
          truncatedCount,
          hasHeader,
          hasError: parsed.length === 0,
        });
        setCsvPreviewRows(previewRows.slice(0, 200));

        setCsvLoading(false);
        resetInput?.();
      },
      error: () => {
        setCsvImportSummary({
          fileName: file.name,
          importedCount: 0,
          invalidEmailCount: 0,
          duplicateCount: 0,
          ignoredRowCount: 0,
          truncatedCount: 0,
          hasHeader: false,
          hasError: true,
        });
        setCsvPreviewRows([]);
        setCsvLoading(false);
        resetInput?.();
      },
    });
  };

  const addInvitationsCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event?.target?.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    parseInvitationsFile(file, () => {
      event.target.value = '';
    });
  };

  const addInvitationsCSVFromDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    parseInvitationsFile(acceptedFiles[0]);
  };

  const handleSendInvitationsProceed = async () => {
    setSendInvitationsState((prev) => ({ ...prev, loading: true }));
    const allSurveys = sendInvitationsFormState.map(
      (form) =>
        new SurveyPayload({
          email: form.email.text,
          type: sendInvitationsState.selectedSurveyType,
          tag: [sendInvitationsState.selectedFolder],
          groupType: 'classic',
          ...(form.firstName.text && { firstname: form.firstName.text }),
          ...(form.lastName.text && { lastname: form.lastName.text }),
        })
    );

    const allResults = await sendOpenSurvey(allSurveys, sendInvitationsState.notSendEmail);
    const surveyTags = await getSurveyTags();

    if (allResults && surveyTags.data) {
      fetchSurveys(surveyTags.data);
    } else {
      handleOpenErrorDialog(undefined, '/surveys', 'post');
      handleOpenErrorDialog(undefined, '/surveys/tags/with-surveys', 'get');
    }
    setSendInvitationsState({ open: false, selectedFolder: '', selectedSurveyType: ImageTypes.CLASSIC_IMAGE, loading: false, notSendEmail: false });
    setSendInvitationsFormState([{ ...EMPTY_FORM }]);
    setCsvImportSummary(null);
    setCsvPreviewRows([]);
  };

  const handleNotSendEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSendInvitationsState((prev) => ({ ...prev, notSendEmail: event.target.checked }));
  };

  const buildExportFile = async (rows: ExportSurveyRow[], filePrefix: string) => {
    try {
      const trimmedDynamicRoute = dynamicRoute.replace(/\/+$/, '');
      const tenantSuffix = tenant ? `?tenantId=${tenant}` : '';
      const worksheetData: string[][] = [
        ['Email', 'First Name', 'Last Name', 'Folder', 'Survey Type', 'Invite Link', 'Survey ID', 'Email Sent', 'Completed'],
      ];

      const inviteLinks = await Promise.all(
        rows.map(async ({ survey }) => {
          const fallbackLink = `${trimmedDynamicRoute}/${survey.id}${tenantSuffix}`;
          const inviteLinkResult = await getSurveyInviteLink(survey.id);
          if (!('error' in inviteLinkResult) && inviteLinkResult.url) {
            return inviteLinkResult.url;
          }
          return fallbackLink;
        })
      );

      rows.forEach(({ survey, tagName }, index) => {
        const inviteLink = inviteLinks[index];
        worksheetData.push([
          survey.email ?? '',
          survey.firstname ?? '',
          survey.lastname ?? '',
          tagName ?? '',
          survey.type ?? '',
          inviteLink,
          survey.id ?? '',
          survey.emailSent ? 'Yes' : 'No',
          survey.completed ? 'Yes' : 'No',
        ]);
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Invite Links');

      const now = new Date();
      const fileName = `${filePrefix}-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      handleOpenErrorDialog(error as any, '/surveys/exportInviteLinks', 'get');
    }
  };

  const getAllExportRows = (): ExportSurveyRow[] => {
    const rows: ExportSurveyRow[] = [];
    tagsState.tagsOrder.forEach((tagIndex) => {
      const tag = tagsState.tags[tagIndex];
      if (!tag) return;
      tag.surveys.forEach((survey) => {
        rows.push({ survey, tagName: tag.tagName });
      });
    });
    return rows;
  };

  const getSelectedExportRows = (): ExportSurveyRow[] => {
    const rows: ExportSurveyRow[] = [];
    tagsState.tagsOrder.forEach((tagIndex) => {
      const tag = tagsState.tags[tagIndex];
      if (!tag) return;
      tag.surveys.forEach((survey) => {
        if (selectionState[survey.id]) {
          rows.push({ survey, tagName: tag.tagName });
        }
      });
    });
    return rows;
  };

  const handleExportInviteLinks = () => {
    buildExportFile(getAllExportRows(), 'survey-invite-links');
  };

  const handleExportSelectedInviteLinks = () => {
    buildExportFile(getSelectedExportRows(), 'survey-invite-links-selected');
  };

  const selectedInvitationsCount = Object.values(selectionState).filter(Boolean).length;

  // --- Confirm delete handlers ---

  const handleOpenConfirmDeleteSurveyDialog = (currentTag: TagModel) => () => {
    setConfirmDeleteState({ open: true, selectionCount: getSelectionCount(currentTag.tagId), currentTag });
  };

  const handleCloseConfirmDeleteSurveyDialog = () => {
    setConfirmDeleteState({ open: false, selectionCount: 0, currentTag: undefined });
  };

  return {
    // State
    loading,
    csvLoading,
    tagsState,
    draggedAnalysisId,
    selectionState,
    dynamicRoute,
    token,

    // Send invitations
    sendInvitationsState,
    sendInvitationsFormState,
    onSendInvitations,
    handleSendInvitationsClose,
    handleSelectedFolderChange,
    handleSelectedSurveyTypeChange,
    formChangeHandlerGenerator,
    formChangeHandleDeleteGenerator,
    handleExportInviteLinks,
    handleExportSelectedInviteLinks,
    selectedInvitationsCount,
    addInvitations,
    clearInvitations,
    addInvitationsCSV,
    addInvitationsCSVFromDrop,
    handleSendInvitationsProceed,
    handleNotSendEmailChange,

    // Tag management
    newTagDialogState,
    editTagDialogState,
    handleNewTagClick,
    handleNewTagChange,
    handleNewTagDialogClose,
    handleNewTagProceed,
    handleEditTagClick,
    handleEditTagChange,
    handleEditTagClose,
    handleEditTagProceed,
    handleToggleVisibility,
    handleTagDelete,

    // Drag & drop
    handleOnDragStart,
    handleOnDragEnd,
    handleCheckboxClick,

    // Move dialog
    surveysMoveState,
    handleMoveDialogConfirm,
    handleMoveDialogClose,

    // Delete dialog
    confirmDeleteState,
    handleOpenConfirmDeleteSurveyDialog,
    handleCloseConfirmDeleteSurveyDialog,
    handleAnalysesDelete,
    csvImportSummary,
    csvPreviewRows,

    // Translation
    t,
  };
}
